import InfoIcon from '@material-ui/icons/Info';
import { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Button, InputNumber, Tooltip, Whisper } from 'rsuite';
import { setDateTypesRecorrencia, setGenerateReportData, setIsSpecificDates, setStateValue } from '../../../../../actions/actionsModuloRelatorio';
import infoIcon from '../../../../../assets/icons/icon_info-gray.svg';
import SelectPill from '../../../../../components/SelectPill';
import { SelectDateRangePill } from '../Components/SelectDateRangePill';
import { brokenPeriodRange, brokenPeriodRangeEnumType, parseDateInTimezone, periodRange, setLocalDateISOFormat } from '../utils';

function isPreSetIntervalDays(number) {
  const preSetValues = ['7', '15', '30', '60', '90', ''];
  return !preSetValues.includes(number);
}

const DateIntervals = () => {
  const [lastDays, setLastDays] = useState('7');
  const [activeBrokenPeriod, setActiveBrokenPeriod] = useState('Dias');
  const [dateRangeValue, setDateRangeValue] = useState([]);
  const [isEditSpecificDate, setIsEditSpecificDate] = useState(false);

  const { id } = useParams();
  const hasUrlID = typeof id === 'string';

  const dispatch = useDispatch();

  const { moduloRelatorioData, editSpecificDateRangeValue } = useSelector(
    state => ({
      moduloRelatorioData: state.moduloRelatorioDataReducer?.moduloRelatorioData,
      editSpecificDateRangeValue: state.moduloRelatorioDataReducer?.editSpecificDateRangeValue,
    }),
    shallowEqual,
  );

  const handlePreSetIntervalDays = number => {
    const data = {
      dataInicio: null,
      dataFim: null,
      dataEspecifica: false,
      isSpecificDates: false,
      editSpecificDateRangeValue: [],
      periodo: Number(number),
    };

    setLastDays(number);
    setDateRangeValue([]);
    dispatch(setDateTypesRecorrencia(data));
    setIsEditSpecificDate(false);
  };

  const handleDateRangePicker = value => {
    if (value.length === 0) return;
    setLastDays('');
    setDateRangeValue(value);
    dispatch(setGenerateReportData('dataInicio', setLocalDateISOFormat(value[0])));
    dispatch(setGenerateReportData('dataFim', setLocalDateISOFormat(value[1])));
    dispatch(setIsSpecificDates(true));
  };

  // EDIT specific date change
  const handleEditSpecificDateRangePicker = value => {
    const data = {
      dataInicio: setLocalDateISOFormat(value[0]),
      dataFim: setLocalDateISOFormat(value[1]),
      dataEspecifica: true,
      recorrencia: false,
      isSpecificDates: true,
      editSpecificDateRangeValue: value,
      periodo: null,
    };
    dispatch(setDateTypesRecorrencia(data));
    setLastDays('');
    setIsEditSpecificDate(true);
  };

  const handleBrokenPeriod = period => {
    setActiveBrokenPeriod(period);
    dispatch(setGenerateReportData('quebraPeriodo', period === 'Todo período' ? 'TODO_PERIODO' : period.toUpperCase()));
  };

  const handleIntervalDays = number => {
    dispatch(setGenerateReportData('periodo', Number(number)));
  };

  // Ao editar relatório salvo, seta valores de período
  useEffect(() => {
    if (!hasUrlID || isEditSpecificDate) return;

    const { quebraPeriodo, periodo, dataEspecifica, dataInicio, dataFim } = moduloRelatorioData;

    // Ajusta período quebrado
    setActiveBrokenPeriod(brokenPeriodRangeEnumType[quebraPeriodo]);

    const isNumberPeriod = typeof periodo === 'number';
    const hasSpecificDate = Boolean(dataEspecifica);

    if (isNumberPeriod && !hasSpecificDate) {
      const periodoStr = periodo.toString();
      setLastDays(periodoStr);
      dispatch(setStateValue('editSpecificDateRangeValue', []));

      return;
    }

    // Caso contrário, intervalo específico
    dispatch(setStateValue('editSpecificDateRangeValue', [parseDateInTimezone(dataInicio), parseDateInTimezone(dataFim)]));
    setLastDays('');
  }, [id, hasUrlID, isEditSpecificDate, moduloRelatorioData, dispatch]);

  return (
    <>
      <div className="section-period">
        <div className="section-title-wrapper">
          <p>Período</p>
          <Whisper placement="right" speaker={<Tooltip>Que será considerado para gerar o relatório sendo 90 dias o máximo possível</Tooltip>}>
            <img src={infoIcon} alt="" />
          </Whisper>
        </div>
        <div className="buttons-wrapper">
          {periodRange.map(item => (
            <Button className={`inf-pill-button ${lastDays === item ? 'active' : ''}`} key={item} onClick={() => handlePreSetIntervalDays(item)}>
              Últimos {item} dias
            </Button>
          ))}
          {hasUrlID ? (
            <SelectPill
              id="select-picker-pill"
              className={`select-interval ${isPreSetIntervalDays(lastDays) ? 'active' : ''}`}
              menuClassName="select-interval-menu"
              placeholder="Intervalo personalizado"
              searchable={false}
              onClose={() => handleIntervalDays(lastDays)}
              renderExtraFooter={() => (
                <div className="select-interval-input-wrapper">
                  <p>Últimos</p>
                  <InputNumber min={0} value={lastDays} onChange={value => setLastDays(value)} />
                  <p>dias</p>
                </div>
              )}
            />
          ) : (
            <SelectDateRangePill
              classNameValue={dateRangeValue.length ? 'date-range-has-value' : ''}
              value={dateRangeValue}
              onChange={value => handleDateRangePicker(value)}
              onClean={() => handlePreSetIntervalDays('7')}
            />
          )}
          {hasUrlID ? (
            <SelectDateRangePill
              classNameValue={editSpecificDateRangeValue.length ? 'date-range-has-value' : ''}
              value={editSpecificDateRangeValue}
              onClean={() => handlePreSetIntervalDays('7')}
              onOk={value => handleEditSpecificDateRangePicker(value)}
              rightIcon={
                <Whisper placement="right" speaker={<Tooltip>Apenas datas dos últimos 90 dias estão disponíveis para seleção.</Tooltip>}>
                  <InfoIcon
                    style={{
                      width: '16px',
                      color: editSpecificDateRangeValue.length ? '#378EF0' : '#959595',
                    }}
                  />
                </Whisper>
              }
            />
          ) : null}
        </div>
      </div>
      <div>
        <div className="section-title-wrapper">
          <p>Quebra de período</p>
          <Whisper placement="right" speaker={<Tooltip>Como a coluna Período virá no relatório: se por dias ou agrupado por semanas ou meses</Tooltip>}>
            <img src={infoIcon} alt="" />
          </Whisper>
        </div>
        <div className="buttons-wrapper">
          {brokenPeriodRange.map(item => (
            <Button className={`inf-pill-button ${activeBrokenPeriod === item ? 'active' : ''}`} key={item} onClick={() => handleBrokenPeriod(item)}>
              {item}
            </Button>
          ))}
        </div>
      </div>
    </>
  );
};

export default DateIntervals;
